'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { apiFetch } from '../lib/api';
import { useTranslation } from '../lib/i18n';
import { useAuth } from '../lib/auth';
import { useExitAnimation } from '../lib/useExitAnimation';
import { searchPages } from '../lib/pageSearch';

// Pozn.: useTranslation() vrací { locale, changeLocale, t, tLocale }.
// Zde používáme pouze t (funkci pro překlad).

export type SearchResult = {
  type: string;
  id: number;
  title: string;
  subtitle?: string;
  url: string;
  icon?: string;
  permission?: string;
};

type GlobalSearchModalProps = {
  open: boolean;
  onClose: () => void;
};

const TYPE_LABEL_KEYS: Record<string, string> = {
  lead: 'searchTypeLead',
  opportunity: 'searchTypeOpportunity',
  case: 'searchTypeCase',
  contact: 'searchTypeContact',
  account: 'searchTypeAccount',
  owner: 'searchTypeOwner',
  'armorik-invoice': 'searchTypeArmorikInvoice',
  'armorik-order': 'searchTypeArmorikOrder',
  product: 'searchTypeProduct',
  'pohoda-order': 'searchTypePohodaOrder',
  'pohoda-invoice': 'searchTypePohodaInvoice',
  page: 'searchTypePage',
};

export default function GlobalSearchModal({ open, onClose }: GlobalSearchModalProps) {
  const router = useRouter();
  const { t, tLocale } = useTranslation();
  const { role, permissions } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const exiting = useExitAnimation(open);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqIdRef = useRef(0);

  // Reset stavu při otevření
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setLoading(false);
      setError(null);
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setResults([]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    const myReqId = ++reqIdRef.current;
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await apiFetch<SearchResult[]>(
          `/api/user/search?q=${encodeURIComponent(query.trim())}&limitPerType=5`
        );
        // ignoruj odpovědi ze starších requestů
        if (myReqId === reqIdRef.current) {
          setResults(data || []);
          setActiveIndex(0);
        }
      } catch (e: any) {
        if (myReqId === reqIdRef.current) {
          setError(e?.message || t('globalSearchError'));
          setResults([]);
        }
      } finally {
        if (myReqId === reqIdRef.current) setLoading(false);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open]);

  // Lokální stránkové výsledky (stránky aplikace — backend hledá jen entity)
  const pageResults = useMemo<SearchResult[]>(
    () => searchPages(query, t, (k) => tLocale('en', k), role, permissions),
    [query, t, tLocale, role, permissions],
  );

  // Plochý seznam pro klávesnicovou navigaci — stránky první, pak entity z backendu
  const flat = useMemo(() => [...pageResults, ...results], [pageResults, results]);

  // Skupiny výsledků podle typu (v pořadí, v jakém je vrátil backend)
  const grouped = useMemo(() => {
    const groups: { type: string; items: SearchResult[] }[] = [];
    const order: string[] = [];
    const byType: Record<string, SearchResult[]> = {};
    for (const r of flat) {
      if (!byType[r.type]) {
        byType[r.type] = [];
        order.push(r.type);
      }
      byType[r.type].push(r);
    }
    for (const type of order) {
      groups.push({ type, items: byType[type] });
    }
    return groups;
  }, [flat]);

  const navigate = useCallback(
    (r: SearchResult) => {
      onClose();
      router.push(r.url);
    },
    [onClose, router]
  );

  // Klávesnice: šipky, Enter, Escape
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const r = flat[activeIndex];
      if (r) navigate(r);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  // Scroll aktivního prvku do view
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector(`[data-idx="${activeIndex}"]`);
    if (el && 'scrollIntoView' in el) {
      (el as HTMLElement).scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex, open]);

  // Zavření na klik backdrop
  const handleBackdrop = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!open && !exiting) return null;

  const typeLabel = (type: string) => {
    const key = TYPE_LABEL_KEYS[type];
    return key ? t(key) : type;
  };

  return (
    <div
      className={`fixed inset-0 z-[10001] flex items-start justify-center bg-bark/40 backdrop-blur-sm pt-24 px-4 ${open ? 'animate-backdropIn' : 'animate-backdropOut'}`}
      onMouseDown={handleBackdrop}
    >
      <div
        className={`bg-linen border border-wheat rounded-rustic shadow-2xl w-full max-w-2xl overflow-hidden ${open ? 'animate-modalIn' : 'animate-modalOut'}`}
        onKeyDown={handleKeyDown}
      >
        {/* Vyhledávací pole */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-wheat">
          <svg className="w-5 h-5 text-wood flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('globalSearchPlaceholder')}
            className="flex-1 bg-transparent text-bark placeholder:text-wood/60 text-base focus:outline-none"
            autoComplete="off"
            spellCheck={false}
          />
          {loading && (
            <span className="text-xs text-wood animate-pulse">…</span>
          )}
          <kbd className="hidden sm:inline-block text-xs text-wood bg-cream border border-wheat rounded px-1.5 py-0.5">
            Esc
          </kbd>
        </div>

        {/* Výsledky */}
        <div ref={listRef} className="max-h-[60vh] overflow-y-auto">
          {error && (
            <div className="px-4 py-6 text-center text-sm text-rust">{error}</div>
          )}

          {!error && !loading && query.trim().length >= 2 && flat.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-wood">
              {t('globalSearchNoResults')}
            </div>
          )}

          {!error && query.trim().length < 2 && (
            <div className="px-4 py-8 text-center text-sm text-wood">
              {t('globalSearchMinChars')}
            </div>
          )}

          {grouped.map((group) => (
            <div key={group.type} className="border-b border-wheat/40 last:border-b-0">
              <div className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-wood bg-cream/40">
                {typeLabel(group.type)}
              </div>
              {group.items.map((r) => {
                const idx = flat.indexOf(r);
                const active = idx === activeIndex;
                return (
                  <button
                    key={`${r.type}-${r.id}`}
                    data-idx={idx}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => navigate(r)}
                    className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition ${
                      active ? 'bg-moss/15' : 'hover:bg-cream/60'
                    }`}
                  >
                    <span className="text-xl flex-shrink-0">{r.icon || '•'}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-bark truncate">
                        {r.title}
                      </span>
                      {r.subtitle && (
                        <span className="block text-xs text-wood truncate">
                          {r.subtitle}
                        </span>
                      )}
                    </span>
                    {active && (
                      <kbd className="text-xs text-wood bg-cream border border-wheat rounded px-1.5 py-0.5 flex-shrink-0">
                        ↵
                      </kbd>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-wheat bg-cream/30 text-xs text-wood flex items-center justify-between">
          <span>
            <kbd className="bg-linen border border-wheat rounded px-1 py-0.5">↑</kbd>{' '}
            <kbd className="bg-linen border border-wheat rounded px-1 py-0.5">↓</kbd>{' '}
            {t('globalSearchNavigate')}
            {' · '}
            <kbd className="bg-linen border border-wheat rounded px-1 py-0.5">↵</kbd>{' '}
            {t('globalSearchOpen')}
          </span>
          <span className="text-wood/70">{t('globalSearchRespectsPermissions')}</span>
        </div>
      </div>
    </div>
  );
}
